import { contactsExportType } from '../const/contactsExportType.js'
import { ExportMetadataFactory } from '../export/exportMetadataFactory.js'
import { EmptySearchPageError } from '../common/errors/EmptySearchPageError.js'
import { getContactsFromSalesNavigatorLeadsList } from '../linkedInExportApi.js'
import type {
	ContactsIteratorResult,
	ExportMetadataItem,
	IBroker
} from './types.js'

interface SearchRequest {
	queryParameters: Record<string, string>
	start: number
	pageSize: number
	listId?: string
}

export class SalesNavigatorLeadsListIterator {
	readonly #broker: IBroker
	#canNext = true
	readonly #searchRequest: SearchRequest
	#total: number | undefined
	readonly #pageSize = 25
	readonly #originPageUri: URL
	readonly #metadata: ExportMetadataItem[]

	constructor(broker: IBroker, uri: URL) {
		this.#broker = broker
		this.#originPageUri = uri
		this.#metadata = ExportMetadataFactory.create(uri)
		const searchParameters = ExportMetadataFactory.getSearchParamsDictionary(
			uri.search
		) as Record<string, string>
		this.#searchRequest = this.#parseSearchRequest(searchParameters)
		this.#searchRequest.listId = this.#parseListId(uri)
	}

	async getNext(metadata?: object): Promise<ContactsIteratorResult> {
		const response = (await getContactsFromSalesNavigatorLeadsList(
			this.#broker,
			this.#searchRequest,
			this.#originPageUri.toString(),
			metadata
		)) as {
			paging: { total: number; start: number; count: number }
			contacts: unknown[]
		}

		this.#canNext =
			response.paging.total > response.paging.start + response.paging.count
		if (this.#canNext) {
			this.#searchRequest.start += this.#pageSize
		}

		if (this.#total === undefined) {
			if (response.paging.total === 0) {
				throw new EmptySearchPageError(
					`there is no contacts on the search page ${this.#originPageUri}`,
					metadata
				)
			}
			this.#total = response.paging.total
		}

		const result = {
			found: response.contacts,
			processed:
				response.paging.total > response.paging.start + response.paging.count
					? response.paging.count
					: response.paging.total - response.paging.start
		}
		return result
	}

	canMove(): boolean {
		return this.#canNext
	}

	getTotal(): number {
		return this.#total ?? 0
	}

	getMetadata(): ExportMetadataItem[] {
		return this.#metadata
	}

	getType(): string {
		return contactsExportType.salesNavigatorLeadsListPage
	}

	#parseSearchRequest(queryParameters: Record<string, string>): SearchRequest {
		let start = 0
		if (queryParameters.page) {
			const originalPageNumber = queryParameters.page
			const pageNumber = parseInt(originalPageNumber)
			start = (pageNumber - 1) * this.#pageSize
			delete queryParameters.page
		}

		const result = {
			queryParameters: queryParameters,
			start: start,
			pageSize: this.#pageSize
		}
		return result
	}

	#parseListId(uri: URL): string {
		const url = new URL(uri)
		const result = url.pathname.split('/')[4]
		return result
	}
}
